/* © Facilogi. See COPYRIGHT file for full copyright & licensing details. */

import Router, { RawLocation, Route } from "vue-router";
import Vue from "vue";

import { $t, EventBus } from "App/IOC";
import { Users } from "App";

import SignIn from "Web/Views/User/Components/SignIn.vue";
import User from "Web/Views/User/Index.vue";
import Main from "Web/Views/Main/Index.vue";

import NotFound from "Web/Views/NotFound.vue";
import UnderConstruction from "Web/Views/UnderConstruction.vue";
import Home from "Web/Views/Home/Index.vue";
import AgentKb from "Web/Views/AgentKb/Index.vue";
import AgentKbUsers from "Web/Views/AgentKb/Users.vue";
import AgentKbConversations from "Web/Views/AgentKb/Conversations.vue";
import AgentKbUsage from "Web/Views/AgentKb/Usage.vue";
import ApiKeys from "Web/Views/ApiKeys/Index.vue";
import DesignCategories from "Web/Views/DesignCategories/Index.vue";
import Newsletters from "Web/Views/Newsletters/Index.vue";
import NewslettersGroup from "Web/Views/Newsletters/Group.vue";
import NewsletterStatsPage from "Web/Views/Newsletters/StatsPage.vue";

import Products from "Web/Views/Products/Index.vue";
import Product from "Web/Views/Products/Single/Index.vue";
import Customers from "Web/Views/Customers/Index.vue";
import Customer from "Web/Views/Customers/Single/Index.vue";
import CustomerGroupUsers from "Web/Views/Customers/Single/Users.vue";
import UsersView from "Web/Views/Users/Index.vue";
import RolesView from "Web/Views/Roles/Index.vue";
import SingleRole from "Web/Views/Roles/Single/Index.vue";
import ConsoleUsersView from "Web/Views/ConsoleUsers/Index.vue";
import SingleConsoleUser from "Web/Views/ConsoleUsers/Single/Index.vue";
import Activate from "Web/Views/Activate/Index.vue";
import ForgotPassword from "Web/Views/ForgotPassword/Index.vue";
import ResetPassword from "Web/Views/ResetPassword/Index.vue";
import AdsPublications from "Web/Views/AdsPublications/Index.vue";
import AdsPublication from "Web/Views/AdsPublications/Single/Index.vue";
import AdsPublicationSalePointSettings from "Web/Views/AdsPublications/SalePointSettings/Index.vue";
// eslint-disable-next-line max-len
import AdsPublicationSalePointPortals from "Web/Views/AdsPublications/SalePointSettings/Portals.vue";
import AdsPublicationGroups from "Web/Views/AdsPublications/Groups/Index.vue";
import Subscriptions from "Web/Views/Subscriptions/Index.vue";
import Subscription from "Web/Views/Subscriptions/Single/Index.vue";
import Payments from "Web/Views/Payments/Index.vue";

/* Settings (sous-menu dédié). */
import Settings from "Web/Views/Main/Settings/Index.vue";

/* Profil utilisateur (sous-menu dédié comme les Paramètres). */
import Profile from "Web/Views/Main/Profile/Index.vue";
import ProfileGeneral from "Web/Views/Main/Profile/General.vue";
import ProfilePreferences from "Web/Views/Main/Profile/Preferences.vue";
import ProfileAuthentication from "Web/Views/Main/Profile/Authentication.vue";
import GeneralGlobalConfiguration from "Web/Views/Main/Configuration/General.vue";
import YearlyCouponConfiguration from "Web/Views/Main/Configuration/Yearly.vue";
import Coupons from "Web/Views/Coupons/Index.vue";
import Coupon from "Web/Views/Coupons/Single/Index.vue";
import PriceTargets from "Web/Views/PriceTargets/Index.vue";
import PriceTarget from "Web/Views/PriceTargets/Single/Index.vue";

/* If this flag becomes true, and the user tries to navigate away to another
   route, the system will show a confirmation popup to protect against
   accidental data loss (Unsaved changes). */
let showDataLossGuard: boolean = false;

Vue.use( Router );

/* To avoid an issue with $t being empty because it has yet to be injected to IOC because if
   we put this instantiation at the module level here, it will get executed during
   node module importation step, which is before we inject the actual $t() implementation into
   our IOC (Through Web/Main.ts), so we wrap it in a funtion. */
function Get(): Router {
    EventBus.Subscribe( "LockMenuActions", ( value: boolean ): void => {
        showDataLossGuard = value;
    });

    const router = new Router({
        mode: "history",
        base: process.env.BASE_URL,
        routes: [
            {
                path: "/user",
                component: User,
                children: [
                    {
                        path: "",
                        redirect: "signin"
                    },

                    {
                        path: "signin",
                        name: "signin",
                        components: {
                            default: SignIn
                        }
                    }
                ]
            },

            /* L'e-mail d'une newsletter, dans le PB e-mail, en plein ecran ( Fred,
               26/10/2026 ). Charge a la demande : le paquet de l'editeur pese 11 Mo. */
            {
                path: "/newsletters/:groupId(\\d+)/:id(\\d+)/email",
                name: "newsletters-email",
                component: (): Promise<any> => import( "Web/Views/Newsletters/EmailEditor.vue" )
            },

            {
                path: "/",
                component: Main,
                children: [
                    {
                        path: "",
                        component: Home
                    },

                    {
                        path: "under-construction",
                        component: UnderConstruction
                    },

                    {
                        path: "products",
                        component: Products
                    },

                    {
                        path: "products/:id(\\d+)",
                        name: "product",
                        component: Product
                    },

                    {
                        path: "coupons",
                        name: "coupons",
                        component: Coupons
                    },

                    {
                        path: "coupons/:id(\\d+)",
                        name: "coupon",
                        component: Coupon
                    },

                    {
                        path: "price-targets",
                        name: "price-targets",
                        component: PriceTargets
                    },

                    {
                        path: "price-targets/:id(\\d+)",
                        name: "price-target",
                        component: PriceTarget
                    },

                    {
                        path: "ads-publications",
                        component: AdsPublications
                    },

                    {
                        path: "ads-publications/sale-point-settings",
                        name: "ads-publication-sale-point",
                        component: AdsPublicationSalePointSettings
                    },

                    {
                        path: "ads-publications/sale-point-settings/:id(\\d+)",
                        name: "ads-publication-sale-point-portals",
                        component: AdsPublicationSalePointPortals
                    },

                    {
                        path: "ads-publications/:id(\\d+)/groups",
                        name: "ads-publication-groups",
                        component: AdsPublicationGroups
                    },

                    {
                        path: "ads-publications/:id(\\d+)",
                        name: "ads-publication",
                        component: AdsPublication
                    },

                    {
                        path: "customers",
                        component: Customers
                    },

                    {
                        path: "customers/:id(\\d+)",
                        name: "customer",
                        component: Customer
                    },

                    {
                        path: "customers/:id(\\d+)/users",
                        name: "customer-users",
                        component: CustomerGroupUsers
                    },

                    {
                        path: "users",
                        name: "users",
                        component: UsersView
                    },

                    {
                        path: "subscriptions",
                        component: Subscriptions
                    },

                    {
                        path: "api-keys",
                        name: "api-keys",
                        component: ApiKeys
                    },

                    /* La newsletter automatique d'un client ( 21/10/2026 ) : choisir le
                       client, puis activer et regler ses newsletters. */
                    {
                        path: "newsletters",
                        name: "newsletters",
                        component: Newsletters
                    },

                    {
                        path: "newsletters/:groupId(\\d+)",
                        name: "newsletters-group",
                        component: NewslettersGroup
                    },

                    /* Les statistiques d'une newsletter, sur un ecran a part ( Fred,
                       22/10/2026 ). */
                    {
                        path: "newsletters/:groupId(\\d+)/:id(\\d+)/stats",
                        name: "newsletters-stats",
                        component: NewsletterStatsPage
                    },

                    {
                        path: "design-categories",
                        name: "design-categories",
                        component: DesignCategories
                    },

                    {
                        path: "agent-ia",
                        name: "agent-ia",
                        component: AgentKb
                    },

                    {
                        path: "agent-ia/users",
                        name: "agent-ia-users",
                        component: AgentKbUsers
                    },

                    {
                        path: "agent-ia/conversations",
                        name: "agent-ia-conversations",
                        component: AgentKbConversations
                    },

                    {
                        path: "agent-ia/usage",
                        name: "agent-ia-usage",
                        component: AgentKbUsage
                    },

                    {
                        path: "invoicing",
                        component: Payments
                    },

                    {
                        path: "subscriptions/:id(\\d+)",
                        name: "subscription",
                        component: Subscription
                    },

                    {
                        path: "settings",
                        component: Settings,
                        children: [
                            {
                                path: "",
                                redirect: "team"
                            },

                            {
                                path: "team",
                                name: "console-team",
                                component: ConsoleUsersView
                            },

                            {
                                path: "team/:id(\\d+)",
                                name: "console-user",
                                component: SingleConsoleUser
                            },

                            {
                                path: "roles",
                                name: "console-roles",
                                component: RolesView
                            },

                            {
                                path: "roles/:id(\\d+)",
                                name: "console-role",
                                component: SingleRole
                            }
                        ]
                    },

                    {
                        path: "profile",
                        component: Profile,
                        children: [
                            {
                                path: "",
                                redirect: "general"
                            },

                            {
                                path: "general",
                                name: "profile-general",
                                component: ProfileGeneral
                            },

                            {
                                path: "preferences",
                                name: "profile-preferences",
                                component: ProfilePreferences
                            },

                            {
                                path: "authentication",
                                name: "profile-authentication",
                                component: ProfileAuthentication
                            }
                        ]
                    },

                    {
                        path: "configuration",
                        component: GeneralGlobalConfiguration
                    },

                    {
                        path: "yearly-coupon",
                        component: YearlyCouponConfiguration
                    }
                ]
            },

            {
                path: "/activate/:token",
                name: "activate",
                component: Activate
            },

            {
                path: "/forgot-password",
                name: "forgot-password",
                component: ForgotPassword
            },

            {
                path: "/reset-password/:token",
                name: "reset-password",
                component: ResetPassword
            },

            {
                path: "*",
                component: NotFound
            }
        ]
    });

    /* Authentication guards. */
    router.beforeEach( async (
        to: Route,
        from: Route,
        next: ( to?: RawLocation ) => void
    ): Promise<void> => {
        /* This goes through the matched routes from last to first, finding the closest route with a
           title. Eg. if we have /some/deep/nested/route and /some, /deep, and /nested have titles,
           nested's will be chosen. */
        const nearestWithTitle = to.matched.slice().reverse().find(
            ( r ): void => r.meta && r.meta.title
        );

        if( nearestWithTitle )
            document.title = nearestWithTitle.meta.title;

        const authenticated = await Users.CheckAuthentication() === undefined;

        const publicRoutes: string[] = [
            "/user/signin",
            "/activate/:token",
            "/forgot-password",
            "/reset-password/:token"
        ];

        const isPublicRoute = publicRoutes.includes( to.matched[ to.matched.length - 1 ].path );

        if( !isPublicRoute && !authenticated ) {
            next( { path: "/user/signin" } );
            return;

        } else if( isPublicRoute && authenticated ) {
            next( { path: "/" } );
            return;
        }

        if( showDataLossGuard ) {
            EventBus.Publish( "Confirmation", {
                Props: {
                    Message: $t( "GLOBAL_dataLossGuard" ),

                    Actions: [{
                        Id: "continue",
                        Name: $t( "GLOBAL_dataLossGuardContinue" ),
                        Active: true
                    }]
                },

                Events: {
                    Action: (): void => {
                        showDataLossGuard = false;
                        next();
                    }
                }
            });

            return;
        }

        next();
    });

    return router;
}

export default {
    Get
};
